<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useScheduleStore } from '@/stores/schedule.store'
import { useTaskMeta, taskContainerLabel, taskDueClass, taskDueLabel } from '@/composables/tasks'

import TaskCheckButton from '@/components/task/TaskCheckButton.vue'
import TaskTypeChip from '@/components/task/TaskTypeChip.vue'
import ServiceLinkChips from '@/components/shared/ServiceLinkChips.vue'

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  showContainer: {
    type: Boolean,
    default: false,
  },
  showType: {
    type: Boolean,
    default: false,
  },
  highlighted: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  openable: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['toggle', 'open'])

const i18n = getI18n()
const scheduleStore = useScheduleStore()
const { extraValueChips } = useTaskMeta()

const chips = computed(() => extraValueChips(props.task))
const linkedServiceIds = computed(() => [...new Set([...(props.task.assignees ?? []), ...(props.task.services ?? [])])])
const isClickable = computed(() => props.openable && !props.readonly)
</script>

<template>
  <q-item
    class="q-py-xs q-pl-xs q-pr-md task-item"
    :class="{ 'row-highlight': highlighted }"
    :clickable="isClickable"
    @click="emit('open', task)"
  >
    <q-item-section side top class="q-pr-sm">
      <task-check-button :task="task" :disable="readonly" @toggle="emit('toggle', $event)" />
    </q-item-section>
    <q-item-section>
      <q-item-label :class="{ 'task-item--done text-grey-6': task.completed }">{{ task.title }}</q-item-label>
      <q-item-label v-if="task.description" caption lines="2">{{ task.description }}</q-item-label>
      <q-item-label v-if="showContainer" caption>{{ taskContainerLabel(task) }}</q-item-label>
      <q-item-label v-if="chips.length || (showType && task.task_type)" caption class="row q-gutter-xs">
        <task-type-chip v-if="showType && task.task_type" :task-type="scheduleStore.getTaskTypeById(task.task_type)" />
        <q-chip
          v-for="chip in chips"
          :key="chip.key"
          dense
          size="sm"
          class="q-ml-none"
          :label="chip.label"
        >
          <q-tooltip>{{ chip.tooltip }}</q-tooltip>
        </q-chip>
      </q-item-label>
    </q-item-section>
    <q-item-section
      v-if="linkedServiceIds.length || task.due_date"
      side
      top
      class="task-item__side"
    >
      <q-item-label v-if="linkedServiceIds.length">
        <service-link-chips
          :service-ids="linkedServiceIds"
          :warning-ids="task.missing_service_ids ?? []"
          :warning-tooltip="i18n.t('common.tasks.notInProject')"
          justify="flex-end"
          short
        />
      </q-item-label>
      <q-item-label v-if="task.due_date" caption :class="taskDueClass(task)">
        <q-icon :name="task.due_time ? 'mdi-clock-outline' : 'mdi-calendar'" class="q-mr-xs" />
        <span>{{ taskDueLabel(task) }}</span>
      </q-item-label>
    </q-item-section>
  </q-item>
</template>

<style scoped>
.task-item--done {
  text-decoration: line-through;
}

.task-item__side {
  max-width: 60%;
  align-items: flex-end;
}
</style>
